import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { regions } from '@/mocks/regions';

export default function AcademySettings() {
  const { profile, refreshProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [academyName, setAcademyName] = useState(profile?.academy_name || '');
  const [city, setCity] = useState(profile?.city || '');
  const [district, setDistrict] = useState(profile?.district || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const selectedRegion = regions.find((r) => r.city === city);

  const startEdit = () => {
    setAcademyName(profile?.academy_name || '');
    setCity(profile?.city || '');
    setDistrict(profile?.district || '');
    setError('');
    setEditing(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (!academyName.trim()) {
      setError('학원명을 입력해주세요.');
      return;
    }
    if (!city || !district) {
      setError('지역(시/도, 구/군)을 선택해주세요.');
      return;
    }

    setBusy(true);
    try {
      const address = `${city} ${district}`;

      // profiles 업데이트
      const { error: profileErr } = await supabase
        .from('profiles')
        .update({ academy_name: academyName.trim(), city, district, address })
        .eq('id', profile?.id);
      if (profileErr) throw profileErr;

      // academies 테이블도 함께 업데이트 (있을 경우)
      if (profile?.academy_id) {
        const { error: academyErr } = await supabase
          .from('academies')
          .update({ name: academyName.trim(), address })
          .eq('id', profile.academy_id);
        if (academyErr) throw academyErr;
      }

      await refreshProfile();
      setEditing(false);
    } catch (err) {
      setError('저장 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="bg-white rounded-xl border border-background-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-1">
              학원 정보
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="text-sm font-semibold text-foreground-900">
                {profile?.academy_name || '학원명 미설정'}
              </span>
              {profile?.address && (
                <span className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-full">
                  {profile.address}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={startEdit}
            className="whitespace-nowrap inline-flex items-center gap-2 border border-background-300 text-foreground-700 hover:bg-background-100 text-sm font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer"
          >
            <i className="ri-edit-line"></i>
            설정
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-background-200 p-5">
      <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
        학원 정보 설정
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-foreground-700 mb-1.5">학원명</span>
          <input
            type="text"
            name="academy_name"
            required
            value={academyName}
            onChange={(e) => setAcademyName(e.target.value)}
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
            placeholder="예: 미래진로학원"
          />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-sm font-medium text-foreground-700 mb-1.5">시/도</span>
            <select
              name="city"
              required
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                setDistrict('');
              }}
              className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              <option value="">시/도 선택</option>
              {regions.map((r) => (
                <option key={r.city} value={r.city}>
                  {r.city}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-foreground-700 mb-1.5">구/군</span>
            <select
              name="district"
              required
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              disabled={!city}
              className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">구/군 선택</option>
              {selectedRegion?.districts.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="whitespace-nowrap px-4 py-2 text-sm rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={busy}
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-5 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
          >
            {busy ? '저장 중...' : '저장'}
          </button>
        </div>
      </form>
    </div>
  );
}